import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, Users, Target, MessageCircle, Calendar, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Award, Stethoscope,
  BarChart3, PieChart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, Legend
} from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLORS = ['#4A238F', '#7C3AED', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#8B5CF6'];

export default function ReferralAnalytics() {
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [topDoctors, setTopDoctors] = useState([]);
  const [trends, setTrends] = useState([]);
  const [specialtyDistribution, setSpecialtyDistribution] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, topRes, trendsRes, specRes] = await Promise.all([
        api.get(`/referring-doctors/analytics/overview?period=${period}`),
        api.get(`/referring-doctors/analytics/top-doctors?period=${period}&limit=10`),
        api.get(`/referring-doctors/analytics/trends?period=${period}`),
        api.get(`/referring-doctors/analytics/specialty-distribution?period=${period}`)
      ]);
      
      setOverview(overviewRes.data);
      setTopDoctors(topRes.data);
      setTrends(trendsRes.data);
      setSpecialtyDistribution(specRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const StatCard = ({ title, value, change, icon: Icon, color, subtitle }) => (
    <Card className={`bg-gradient-to-br ${color} border-0`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-white/80">{title}</p>
            <p className="text-3xl font-bold text-white mt-1">{value}</p>
            {subtitle && <p className="text-sm text-white/60 mt-1">{subtitle}</p>}
            {change !== undefined && (
              <div className={`flex items-center mt-2 text-sm ${change >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                {change >= 0 ? <ArrowUpRight className="h-4 w-4 mr-1" /> : <ArrowDownRight className="h-4 w-4 mr-1" />}
                {Math.abs(change)}% vs last period
              </div>
            )}
          </div>
          <div className="p-3 bg-white/10 rounded-lg">
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Referral Analytics</h1>
          <p className="text-slate-600">Track referral performance and identify top partners</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px] bg-slate-700/50 border-slate-600 text-white">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={fetchAllData}
            className="border-slate-600 text-slate-300"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Referrals"
          value={overview?.total_referrals || 0}
          change={overview?.referral_change_percent}
          icon={TrendingUp}
          color="from-[#4A238F] to-[#7C3AED]"
        />
        <StatCard
          title="Referring Doctors"
          value={overview?.unique_referring_doctors || 0}
          icon={Users}
          color="from-emerald-500 to-teal-600"
          subtitle={`${overview?.avg_referrals_per_doctor || 0} avg per doctor`}
        />
        <StatCard
          title="Conversion Rate"
          value={`${overview?.conversion_rate || 0}%`}
          icon={Target}
          color="from-amber-500 to-orange-600"
          subtitle={`${overview?.completed_referrals || 0} completed`}
        />
        <StatCard
          title="Messages Sent"
          value={overview?.messages_sent || 0}
          icon={MessageCircle}
          color="from-blue-500 to-indigo-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Referral Trends Chart */}
        <Card className="lg:col-span-2 bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#4A238F]" />
              Referral Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-slate-300">
                No referral data for this period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trends}>
                  <defs>
                    <linearGradient id="colorReferrals" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4A238F" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4A238F" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate} 
                    stroke="#9CA3AF"
                    fontSize={12}
                  />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#F3F4F6' }}
                    itemStyle={{ color: '#D1D5DB' }}
                    labelFormatter={formatDate}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="referrals" 
                    stroke="#4A238F" 
                    fillOpacity={1} 
                    fill="url(#colorReferrals)" 
                    name="Referrals"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="completed" 
                    stroke="#10B981" 
                    fillOpacity={1} 
                    fill="url(#colorCompleted)" 
                    name="Completed"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Specialty Distribution */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="h-5 w-5 text-[#4A238F]" />
              By Specialty
            </CardTitle>
          </CardHeader>
          <CardContent>
            {specialtyDistribution.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-slate-300">
                No data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={specialtyDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="referrals"
                    nameKey="specialty"
                    label={({ specialty, percentage }) => `${specialty}: ${percentage}%`}
                    labelLine={false}
                  >
                    {specialtyDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    formatter={(value, name) => [`${value} referrals`, name]}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Referring Doctors */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-400" />
            Top Referring Doctors
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topDoctors.length === 0 ? (
            <div className="py-12 text-center text-slate-300">
              <Stethoscope className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No referral data for this period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-slate-300 border-b border-slate-700">
                    <th className="pb-3 font-medium">Rank</th>
                    <th className="pb-3 font-medium">Doctor</th>
                    <th className="pb-3 font-medium">Specialty</th>
                    <th className="pb-3 font-medium text-center">Referrals</th>
                    <th className="pb-3 font-medium text-center">Patients</th>
                    <th className="pb-3 font-medium text-center">Completed</th>
                    <th className="pb-3 font-medium text-center">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {topDoctors.map((doctor, index) => (
                    <motion.tr
                      key={doctor.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30"
                    >
                      <td className="py-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                          ${index === 0 ? 'bg-amber-500/20 text-amber-400' : 
                            index === 1 ? 'bg-slate-400/20 text-slate-300' : 
                            index === 2 ? 'bg-orange-600/20 text-orange-400' : 
                            'bg-slate-700 text-slate-400'}`}>
                          {index + 1}
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4A238F] to-[#7C3AED] flex items-center justify-center text-white font-bold">
                            {doctor.name?.charAt(0) || 'D'}
                          </div>
                          <span className="text-white font-medium">{doctor.name}</span>
                        </div>
                      </td>
                      <td className="py-4">
                        <Badge variant="outline" className="border-slate-500 text-slate-200">
                          {doctor.specialty || 'General'}
                        </Badge>
                      </td>
                      <td className="py-4 text-center">
                        <span className="text-purple-400 font-bold text-lg">{doctor.referral_count}</span>
                      </td>
                      <td className="py-4 text-center text-slate-200">
                        {doctor.unique_patients}
                      </td>
                      <td className="py-4 text-center text-emerald-400">
                        {doctor.completed_count}
                      </td>
                      <td className="py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full"
                              style={{ width: `${doctor.conversion_rate}%` }}
                            />
                          </div>
                          <span className="text-slate-200 text-sm">{doctor.conversion_rate}%</span>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
